"use client";

/**
 * DiscoveryFanbasesSheet 컴포넌트
 * @description 팬베이스를 눌렀을 때 — 폰에서는 바텀시트, 넓은 화면에서는 가운데 모달로 요약을
 *   보여주고, 바닥 CTA 로 앱에서 이어 보게 한다. 같은 내용을 두 껍데기에 담을 뿐이라
 *   내용(`DiscoveryFanbasesProfile`)은 하나다.
 *
 *   껍데기 선택은 붙은 뒤의 화면 폭으로 한다 — 처음엔 닫혀 있어 서버 렌더와 어긋날 것이 없다.
 */
import { BottomSheet, Button, Modal } from "@fanwork-web/design-system";
import { memo, useCallback } from "react";

import { discoveryFanbaseProfileStyles } from "./DiscoveryFanbases.styles";
import type { DiscoveryFanbasesSheetProps } from "./DiscoveryFanbases.types";
import DiscoveryFanbasesProfile from "./DiscoveryFanbasesProfile";

import { routes } from "@/shared/config/routes";
import { trackFanbaseAppOpened } from "../../analytics";
import { openInApp } from "@/shared/lib/deepLink";
import { TABLET_QUERY, useMediaQuery } from "@/shared/lib/useMediaQuery";

const COPY = { action: "View in app", label: "Fanbase" } as const;

const DiscoveryFanbasesSheet = memo<DiscoveryFanbasesSheetProps>(
  ({ fanbase, onClose }) => {
    const isTablet = useMediaQuery(TABLET_QUERY);
    // 팬베이스도 조직이라 앱은 조직 주소로 연다 — 사람 프로필(`/profiles/…`)과 다른 자리다.
    const open = useCallback(() => {
      if (!fanbase) return;
      trackFanbaseAppOpened(fanbase);
      openInApp(routes.organizations.detail(fanbase.fanbaseId));
    }, [fanbase]);

    const label = fanbase?.name ?? COPY.label;
    const content = fanbase ? (
      <DiscoveryFanbasesProfile fanbase={fanbase} />
    ) : null;
    const cta = (
      <Button
        text={COPY.action}
        tone="brand"
        radius="circle"
        fullWidth
        onClick={open}
      />
    );

    if (isTablet) {
      return (
        <Modal
          visible={Boolean(fanbase)}
          onClose={onClose}
          label={label}
          footer={cta}
        >
          {content}
        </Modal>
      );
    }
    return (
      <BottomSheet visible={Boolean(fanbase)} onClose={onClose} label={label}>
        {content}
        <div className={discoveryFanbaseProfileStyles.sheetFooter}>{cta}</div>
      </BottomSheet>
    );
  },
);

DiscoveryFanbasesSheet.displayName = "DiscoveryFanbasesSheet";

export default DiscoveryFanbasesSheet;
